import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import Card from '../ui/Card'
import Button from '../ui/Button'
import Badge from '../ui/Badge'
import PageHeader from '../layout/PageHeader'
import ProjectPageTitle from '../layout/ProjectPageTitle'
import { formatClientLocationSubtitle } from '../../utils/projectSubtitle'
import PdfViewer from '../ui/PdfViewer'
import { useProjects } from '../../contexts/ProjectsContext'
import {
  deleteReport,
  getReportDownloadUrl,
  listReports,
  type ReportListItem
} from '../../utils/reportsClient'
import { fetchSectionsWithVersion } from '../../utils/sectionsClient'
import WpsReportHistory from './wpsDetails/WpsReportHistory'
import type { WpsHistoryReport } from './wpsDetails/groupWpsReportsForHistory'
import WpsPqrPickerModal from './wpsPreview/WpsPqrPickerModal'
import { readPqrTrainOptions } from './wpsPreview/readPqrTrainOptions'
import { readWeldOptionsFromSections } from './pqrPreview/PqrWeldPickerModal'
import ConfirmDialog, { type ConfirmDialogTone } from '../ui/ConfirmDialog'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import {
  fetchImportSourcePdfUrl,
  isImportDraftReport,
  isPdfImportPipeSet,
  isUsableReportFileKey,
  triggerBrowserDownload
} from '../../utils/importReportPdf'

const WPS_TAG = 'WPS'
const PQR_TAG = 'PQR'

function isVisibleWps(item: ReportListItem): boolean {
  if (item.tag !== WPS_TAG) return false
  if (typeof item.deletedAt === 'string' && item.deletedAt.trim().length > 0) return false
  return true
}

function isVisiblePqr(item: ReportListItem): boolean {
  if (item.tag !== PQR_TAG) return false
  if (typeof item.deletedAt === 'string' && item.deletedAt.trim().length > 0) return false
  return true
}

function reportWeldIds(report: ReportListItem): string[] {
  if (Array.isArray(report.weldIds) && report.weldIds.length > 0) {
    return report.weldIds.map((s) => s.trim()).filter(Boolean)
  }
  if (typeof report.weldId === 'string' && report.weldId.trim()) {
    return [report.weldId.trim()]
  }
  return []
}

function reportPqrNumbers(report: ReportListItem): string[] {
  if (Array.isArray(report.pqrNumbers) && report.pqrNumbers.length > 0) {
    return report.pqrNumbers.map((s) => s.trim()).filter(Boolean)
  }
  return []
}

function extractionIdOf(report: ReportListItem): string {
  return typeof report.extractionId === 'string' ? report.extractionId.trim() : ''
}

export default function WpsDetails() {
  const { id, pipeId } = useParams()
  const navigate = useNavigate()
  const { getProject } = useProjects()
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)
  const [items, setItems] = useState<ReportListItem[]>([])
  const [pqrReports, setPqrReports] = useState<ReportListItem[]>([])
  const [listLoading, setListLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [viewer, setViewer] = useState<{ fileUrl: string; fileName: string } | null>(
    null
  )
  const [pageConfirm, setPageConfirm] = useState<{
    title: string
    description: string
    confirmLabel: string
    tone?: ConfirmDialogTone
    onConfirm: () => void
  } | null>(null)
  const [pqrTrainOptions, setPqrTrainOptions] = useState<
    ReturnType<typeof readPqrTrainOptions>
  >([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [isImportedPipeSet, setIsImportedPipeSet] = useState(false)
  const [legacyDownloadBusy, setLegacyDownloadBusy] = useState(false)
  const { getSection } = useWpsSections()

  const refresh = useCallback(async () => {
    if (!id || !pipeId) return
    setListLoading(true)
    try {
      const [reportsRes, sectionsRes] = await Promise.all([
        listReports({ projectId: id, pipeSetId: pipeId }),
        fetchSectionsWithVersion(id, pipeId).catch(() => ({
          sections: {} as Record<string, unknown>,
          version: 0
        }))
      ])
      const all = reportsRes.items || []
      const wpsItems = all.filter(isVisibleWps)
      setItems(wpsItems)
      setPqrReports(all.filter(isVisiblePqr))
      const weldOptions = readWeldOptionsFromSections(sectionsRes.sections)
      setPqrTrainOptions(readPqrTrainOptions(all.filter(isVisiblePqr), weldOptions))
      setIsImportedPipeSet(
        isPdfImportPipeSet(sectionsRes.sections.meta) ||
          wpsItems.some((r) => isImportDraftReport(r))
      )
    } catch (err) {
      console.error('Failed to list WPS reports', err)
      toast.error('Could not load WPS report history')
    } finally {
      setListLoading(false)
    }
  }, [id, pipeId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  /** Presign existing S3 object — never calls generate-wps. */
  const openStoredPdf = useCallback(
    async (report: ReportListItem): Promise<{ downloadUrl: string; fileName: string }> => {
      if (!id || !pipeId) throw new Error('Missing project or pipe set')
      if (!isUsableReportFileKey(report.fileKey)) {
        throw new Error('This report has no PDF file yet')
      }
      const { downloadUrl } = await getReportDownloadUrl({
        projectId: id,
        pipeSetId: pipeId,
        reportId: report.id
      })
      return { downloadUrl, fileName: report.name }
    },
    [id, pipeId]
  )

  const handleDownloadLegacyUploaded = useCallback(async () => {
    if (!id || !pipeId || legacyDownloadBusy || busyId) return
    const source =
      items.find((r) => isImportDraftReport(r) && extractionIdOf(r)) ||
      items.find((r) => extractionIdOf(r))
    const extractionId = source ? extractionIdOf(source) : ''
    if (!source || !extractionId) {
      toast.error('Original PDF unavailable — re-import this WPS to view the source')
      return
    }
    setLegacyDownloadBusy(true)
    try {
      const wpsNumber = String(source.wpsNumber || '').trim()
      const revision = String(source.revision || '').trim()
      const fileName = `${wpsNumber || 'WPS'}${revision ? `-Rev-${revision}` : ''}.pdf`
      const { downloadUrl, fileName: resolvedName } = await fetchImportSourcePdfUrl({
        projectId: id,
        pipeSetId: pipeId,
        extractionId,
        kind: 'wps',
        fileName
      })
      try {
        await triggerBrowserDownload(downloadUrl, resolvedName)
      } finally {
        URL.revokeObjectURL(downloadUrl)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not download original PDF')
    } finally {
      setLegacyDownloadBusy(false)
    }
  }, [id, pipeId, items, legacyDownloadBusy, busyId])

  /** Card click always opens editable Preview page (same as manual projects). */
  const handlePreview = useCallback(
    (report: WpsHistoryReport) => {
      if (!project || !pipeSet) return
      if (busyId) return

      const params = new URLSearchParams()
      const weldIds = reportWeldIds(report)
      const pqrNumbers = reportPqrNumbers(report)
      if (weldIds.length > 0) {
        params.set('weldIds', weldIds.join(','))
      }
      if (pqrNumbers.length > 0) {
        params.set('pqrNumbers', pqrNumbers.join(','))
      }
      if (
        weldIds.length === 0 &&
        typeof report.bevelId === 'string' &&
        report.bevelId.trim().length > 0
      ) {
        params.set('bevelId', report.bevelId.trim())
      }
      const seededWpsNumber =
        typeof report.wpsNumber === 'string' ? report.wpsNumber.trim() : ''
      const seededRevision =
        typeof report.revision === 'string' ? report.revision.trim() : ''
      if (seededWpsNumber) params.set('wpsNumber', seededWpsNumber)
      if (seededRevision) params.set('revision', seededRevision)
      if (report.isSynthetic === true) params.set('new', '1')
      const qs = params.toString()
      const url = `/projects/${project.id}/next/${pipeSet.id}/wps/preview${qs ? `?${qs}` : ''}`
      navigate(url)
    },
    [navigate, project, pipeSet, busyId]
  )

  const handleGenerateApply = useCallback(
    (payload: {
      trainKeys: string[]
      pqrNumbers: string[]
      weldIds: string[]
      wpsNumber: string
      revision: string
    }) => {
      if (!project || !pipeSet) return
      setPickerOpen(false)
      const meta = getSection('meta') as { wpsNumber?: unknown } | null
      const wps = getSection('wps') as { wpsNumber?: unknown } | null
      const stored =
        String(payload.wpsNumber || '').trim() ||
        String(meta?.wpsNumber ?? '').trim() ||
        String(wps?.wpsNumber ?? '').trim() ||
        String(pipeSet.wpsNumber ?? '').trim()
      const params = new URLSearchParams({
        new: '1',
        wpsNumber: stored,
        revision: payload.revision
      })
      if (payload.pqrNumbers.length > 0) {
        params.set('pqrNumbers', payload.pqrNumbers.join(','))
      }
      if (payload.weldIds.length > 0) {
        params.set('weldIds', payload.weldIds.join(','))
      }
      navigate(
        `/projects/${project.id}/next/${pipeSet.id}/wps/preview?${params.toString()}`
      )
    },
    [navigate, project, pipeSet, getSection]
  )

  const initialTrainKeys = useMemo(() => {
    return pqrTrainOptions.length === 1 ? [pqrTrainOptions[0].trainKey] : []
  }, [pqrTrainOptions])

  if (!project || !pipeSet) {
    return (
      <Card title="WPS not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  /** Card Download: Studio PDF via download-url + PdfViewer (same as manual). */
  const handleDownload = async (report: ReportListItem) => {
    if (!id || !pipeId || busyId) return
    setBusyId(report.id)
    try {
      const { downloadUrl, fileName } = await openStoredPdf(report)
      setViewer({ fileUrl: downloadUrl, fileName })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not open report')
    } finally {
      setBusyId(null)
    }
  }

  const performRemove = async (report: ReportListItem) => {
    if (!id || !pipeId) return
    setBusyId(report.id)
    const previous = items
    setItems(previous.filter((item) => item.id !== report.id))
    try {
      await deleteReport({ projectId: id, pipeSetId: pipeId, reportId: report.id })
      toast.success('WPS report removed')
    } catch (err) {
      setItems(previous)
      toast.error('Could not remove report')
    } finally {
      setBusyId(null)
    }
  }

  const handleRemove = async (
    report: ReportListItem,
    meta: { isLatest: boolean }
  ) => {
    if (!id || !pipeId) return
    const label = report.wpsNumber || report.name
    const revisionLabel = meta.isLatest
      ? 'latest revision'
      : `revision v${report.revision ?? ''}`
    setPageConfirm({
      title: meta.isLatest ? 'Remove latest revision?' : 'Remove revision?',
      description: `This will soft-delete the ${revisionLabel} of ${label}. It can be recovered if needed.`,
      confirmLabel: 'Remove',
      tone: 'danger',
      onConfirm: () => {
        setPageConfirm(null)
        void performRemove(report)
      }
    })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        backTo={{ to: `/projects/${project.id}/next/${pipeSet.id}`, label: 'Back to WPS' }}
        title={<ProjectPageTitle label="WPS Reports" projectName={project.name} />}
        subtitle={
          <>
            {formatClientLocationSubtitle(project) ? (
              <span>{formatClientLocationSubtitle(project)}</span>
            ) : null}
            <Badge tone="primary">
              {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
              {pipeSet.wallThicknessUnit}
            </Badge>
          </>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {isImportedPipeSet ? (
              <Button
                variant="secondary"
                onClick={() => void handleDownloadLegacyUploaded()}
                disabled={legacyDownloadBusy || busyId != null}
                title="Download legacy uploaded WPS"
                data-testid="wps-history-download-legacy"
              >
                Original PDF
              </Button>
            ) : null}
            <Button onClick={() => setPickerOpen(true)} data-testid="wps-history-generate">
              Generate WPS
            </Button>
          </div>
        }
      />

      <WpsReportHistory
        reports={items}
        busyId={busyId}
        listLoading={listLoading}
        onDownload={(r) => void handleDownload(r)}
        onRemove={(r, meta) => void handleRemove(r, meta)}
        onPreview={handlePreview}
      />

      {viewer && (
        <PdfViewer
          fileUrl={viewer.fileUrl}
          fileName={viewer.fileName}
          onClose={() => {
            if (viewer.fileUrl.startsWith('blob:')) {
              URL.revokeObjectURL(viewer.fileUrl)
            }
            setViewer(null)
          }}
        />
      )}

      <ConfirmDialog
        open={pageConfirm !== null}
        title={pageConfirm?.title ?? ''}
        description={pageConfirm?.description ?? ''}
        confirmLabel={pageConfirm?.confirmLabel ?? 'Confirm'}
        tone={pageConfirm?.tone}
        onConfirm={() => pageConfirm?.onConfirm()}
        onCancel={() => setPageConfirm(null)}
      />

      <WpsPqrPickerModal
        open={pickerOpen}
        trains={pqrTrainOptions}
        wpsReports={items.filter(isVisibleWps)}
        pqrReports={pqrReports}
        initialTrainKeys={initialTrainKeys}
        onApply={handleGenerateApply}
        onCancel={() => setPickerOpen(false)}
      />
    </div>
  )
}
